import {
  codeToolMaxCodeLength,
  codeToolMaxTimeoutMs,
  defaultCodeToolTimeoutMs,
  type CodeToolSettings,
} from "@repo/app-types/entities/tools/code";
import { isFetchBaseQueryError } from "../../../api/helpers/errors.js";
import { formatJavaScript } from "../codeFormatting.js";
import {
  CodeToolForm,
  type CodeToolFormSettings,
} from "../components/codeToolForm.js";
import type { ToolFormConfig } from "./baseFormConfig.js";
import {
  buildCodeToolSecretsWrite,
  buildStoredCodeToolFormSecret,
} from "./codeToolSecrets.js";
import {
  buildToolMock,
  formatToolMockFormSettings,
  initializeToolMockFormSettings,
} from "./toolMockResponse.js";

const starterCode = `export default async function (input, { secrets }) {
  return { message: "Hello" };
}
`;

export const codeToolFormConfig: ToolFormConfig<
  CodeToolSettings,
  CodeToolFormSettings
> = {
  initialize: (settings, secrets) => ({
    code: settings?.code ?? starterCode,
    codeError: undefined,
    codeErrorPosition: undefined,
    parameters: settings?.parameters ?? [],
    allowedDomains: settings?.allowed_domains ?? [],
    allowedDomainsError: undefined,
    timeout: (settings?.timeout_ms ?? defaultCodeToolTimeoutMs).toString(),
    timeoutError: undefined,
    secrets: secrets.map(buildStoredCodeToolFormSecret),
    storedSecrets: secrets,
    ...initializeToolMockFormSettings(settings?.mock),
  }),
  validate: (settings) => settings.code.trim() !== "",
  save: (settings) => {
    if (settings.code.trim() === "") {
      return {
        isValid: false,
        formSettings: { codeError: "Write the script to run." },
      };
    }
    if (settings.code.length > codeToolMaxCodeLength) {
      return {
        isValid: false,
        formSettings: {
          codeError: `The code can be at most ${codeToolMaxCodeLength.toLocaleString()} characters long.`,
        },
      };
    }

    const timeout = settings.timeout.trim();
    const timeoutMs = timeout === "" ? undefined : Number(timeout);
    if (
      timeoutMs !== undefined &&
      (!Number.isInteger(timeoutMs) ||
        timeoutMs <= 0 ||
        timeoutMs > codeToolMaxTimeoutMs)
    ) {
      return {
        isValid: false,
        formSettings: {
          timeoutError: `Enter a whole number of milliseconds up to ${codeToolMaxTimeoutMs.toLocaleString()}.`,
        },
      };
    }

    const secretsResult = buildCodeToolSecretsWrite(
      settings.secrets,
      settings.storedSecrets,
      settings.allowedDomains,
    );
    if (!secretsResult.isValid) {
      return {
        isValid: false,
        formSettings: { secrets: secretsResult.secrets },
      };
    }

    const mockResult = buildToolMock(settings);
    if (!mockResult.isValid) {
      return {
        isValid: false,
        formSettings: { mockResponseError: mockResult.mockResponseError },
      };
    }

    const parameters = settings.parameters.filter((p) => p.name.trim() !== "");

    return {
      isValid: true,
      settings: {
        code: settings.code,
        parameters: parameters.length > 0 ? parameters : undefined,
        allowed_domains: [...settings.allowedDomains],
        timeout_ms: timeoutMs,
        mock: mockResult.mock,
      },
      secrets: secretsResult.write,
    };
  },
  readSaveError: (error) => {
    // The API compiles the script on save, and its message says where it fails.
    const data = isFetchBaseQueryError(error) ? error.data : undefined;
    if (
      typeof data === "object" &&
      data !== null &&
      "code" in data &&
      data.code === "code_does_not_compile" &&
      "message" in data &&
      typeof data.message === "string"
    ) {
      const line = "line" in data ? data.line : undefined;
      const column = "column" in data ? data.column : undefined;
      return {
        codeError: data.message,
        codeErrorPosition:
          typeof line === "number" && typeof column === "number"
            ? { line, column }
            : undefined,
      };
    }
    return undefined;
  },
  readParameters: (settings) => settings.parameters,
  formatSettings: async (settings) => {
    const code = await formatJavaScript(settings.code);
    return {
      ...(code === settings.code ? {} : { code }),
      ...formatToolMockFormSettings(settings),
    };
  },
  readProposedSettings: (settings) => ({
    ...(settings.code === undefined
      ? {}
      : {
          code: settings.code,
          codeError: undefined,
          codeErrorPosition: undefined,
        }),
    ...(settings.parameters ? { parameters: settings.parameters } : {}),
    ...(settings.allowed_domains
      ? {
          allowedDomains: settings.allowed_domains,
          allowedDomainsError: undefined,
        }
      : {}),
    ...(settings.timeout_ms === undefined
      ? {}
      : { timeout: settings.timeout_ms.toString(), timeoutError: undefined }),
    ...(settings.mock ? initializeToolMockFormSettings(settings.mock) : {}),
  }),
  Form: CodeToolForm,
};
